import {ButtonBar, useSnackbar} from "@dain/mui-components";
import {Stack} from "@mui/material";
import {useMutation, useQuery} from "@tanstack/react-query";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {DASHBOARD, SUBMISSIONS} from "../../../AppRoutes";
import {useClientLocator} from "../../../client/ApiClientLocator";
import {CatalogType} from "../../../client/catalog/CatalogType";
import {PaymentInformationFormat} from "../../../client/payment/PaymentInformationApiClient";
import {SubmissionAttachmentType, SubmissionDto} from "../../../client/submission/SubmissionApiClient";
import {
    DigitalActionAuthorizationInitResultDto,
    DigitalActionAuthorizationResult
} from "../../../client/submission/SubmissionAuthorizationApiClient";
import Button from "../../../common/button/Button";
import {CommonTranslKey} from "../../../common/CommonTranslKey";
import {DateTimeFormatter} from "../../../common/DateTimeFormatter";
import {DetailsTable} from "../../../common/details/DetailsTable";
import {DetailsTextRow} from "../../../common/details/DetailsTextRow";
import Page from "../../../common/page/Page";
import {BlobUtils} from "../../../util/BlobUtils";
import {PaymentTranslKey} from "../PaymentTranslKey";
import {SubmissionTranslKey} from "../SubmissionTranslKey";
import {
    authorizationRetry,
    AUTHORIZATION_RETRY_DELAY_MS,
    isAuthorizationServiceUnavailableError,
    isSubmissionNotReadyForAuthorizationError
} from "./AuthorizationRetryPolicy";
import AuthorizationServiceUnavailableDialog from "./AuthorizationServiceUnavailableDialog";
import SubmissionNotReadyForAuthorizationDialog from "./SubmissionNotReadyForAuthorizationDialog";
import {DigitalActionAuthorizationResultFormatter} from "./DigitalActionAuthorizationResultTypeFormatter";
import {PaymentInformationPanel} from "./payment/PaymentInformationPanel";
import {usePaymentInformationQuery} from "./payment/PaymentInformationQuery";
import {usePaymentRequiredQuery} from "./payment/RequirePaymentQuery";

const CERT_NOT_STAMPED_FALLBACK_DELAY_MS = 2 * 60 * 1000;
const CERT_ATTACHMENT_POLL_INTERVAL_MS = 10_000;

export function SubmissionAuthorizationResultPage() {
    const {t} = useTranslation();
    const snackbar = useSnackbar();
    const {submissionClient, submissionAuthorizationClient, paymentInformationApiClient} = useClientLocator();
    const navigate = useNavigate();

    const {submissionId} = useParams();

    const initialized = useRef(false);
    const [submission, setSubmission] = useState<SubmissionDto>();

    const [certButtonInProgress, setCertButtonInProgress] = useState(false);
    const [certNotStampedEnabled, setCertNotStampedEnabled] = useState(false);
    const [showUnavailableDialog, setShowUnavailableDialog] = useState(false);
    const [showNotReadyDialog, setShowNotReadyDialog] = useState(false);

    const {mutate: downloadPaymentInformationPdf, isPending: pdfInProgress} = useMutation({
        mutationFn: (submissionId: number) => paymentInformationApiClient.downloadPaymentInformationPdf(submissionId),
        onSuccess: (blob) => BlobUtils.downloadBlob(blob, `platebni-udaje-${submission?.submissionNumber}.pdf`),
        onError: (err) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err),
    });

    const reinitMutation = useMutation<DigitalActionAuthorizationInitResultDto | undefined, unknown, number>({
        mutationFn: (submissionId) => submissionAuthorizationClient.reinitDigitalActionAuthorization(submissionId + ""),
        retry: authorizationRetry,
        retryDelay: AUTHORIZATION_RETRY_DELAY_MS,
        onSuccess: (result) => {
            if (result && result.result === DigitalActionAuthorizationResult.SUCCESS && result.postInitRedirectUri) {
                window.location.href = result.postInitRedirectUri;
            } else {
                snackbar.error(t(SubmissionTranslKey.AUTHORIZATION_REINITALIZATION_FAILED), "Reinitialization failed");
            }
        },
        onError: (err) => {
            if (isAuthorizationServiceUnavailableError(err)) {
                setShowUnavailableDialog(true);
            } else if (isSubmissionNotReadyForAuthorizationError(err)) {
                setShowNotReadyDialog(true);
            } else {
                snackbar.error(t(SubmissionTranslKey.AUTHORIZATION_REINITALIZATION_FAILED), err);
            }
        },
    });

    const submissionIdIsValid = submissionId && !isNaN(+submissionId);

    const authorizationSucceeded = submission?.digitalActionAuthorization?.authorizationResult === DigitalActionAuthorizationResult.SUCCESS;

    const {data: submissionWithPaymentRequired} = usePaymentRequiredQuery(submission?.id, submissionClient, authorizationSucceeded);
    const {data: paymentInformation} = usePaymentInformationQuery(
        submission?.id,
        submissionWithPaymentRequired?.paymentRequired,
        paymentInformationApiClient,
        authorizationSucceeded
    );

    useEffect(() => {
        if (submissionIdIsValid && !initialized.current) {
            initialized.current = true;
            submissionClient.findById(+submissionId)
                .then((submission) => {
                    if (submission && submission.digitalActionAuthorization && submission.digitalActionAuthorization.authorizationResult) {
                        setSubmission(submission);
                    } else {
                        navigate(SUBMISSIONS);
                    }
                })
                .catch((err) => {
                    snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err);
                });
        }
    }, [submissionIdIsValid, submissionId, submissionClient, navigate, t, snackbar]);

    useEffect(() => {
        const timer = setTimeout(() => setCertNotStampedEnabled(true), CERT_NOT_STAMPED_FALLBACK_DELAY_MS);
        return () => clearTimeout(timer);
    }, []);

    const certQueryEnabled = !!submissionIdIsValid
        && !certNotStampedEnabled
        && authorizationSucceeded;

    const certificationAttachmentResult = useQuery({
        queryKey: ['submissionCert', submissionIdIsValid, submissionId],
        queryFn: () => new Promise((resolve, reject) => {
            if (submissionIdIsValid) {
                submissionClient.findFirstAttachmentByType(+submissionId, SubmissionAttachmentType.CERTIFICATION)
                    .then(attachment => {
                        if (attachment) {
                            resolve(attachment);
                        } else {
                            reject();
                        }

                    })
                    .catch(() => reject());
            } else {
                resolve(null);
            }
        }),
        enabled: certQueryEnabled,
        retry: false,
        refetchInterval: (query) => query.state.status === 'success' ? false : CERT_ATTACHMENT_POLL_INTERVAL_MS,
    });

    function downloadSubmissionCertification(submissionId: number, documentName: string) {
        setCertButtonInProgress(true);
        submissionClient.downloadSubmissionCertificationDocument(submissionId)
            .then((documentBlob) => {
                const fileName = `${documentName}_${t(SubmissionTranslKey.CERTIFICATION)}.pdf`;
                BlobUtils.downloadBlob(documentBlob, fileName);
            })
            .catch((err) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err))
            .finally(() => {
                setCertButtonInProgress(false);
            });
    }

    function downloadCertificationNotStamped(submissionId: number, documentName: string) {
        setCertButtonInProgress(true);
        submissionClient.downloadSubmissionCertificationDocumentNotStamped(submissionId)
            .then((documentBlob) => {
                const fileName = `${documentName}_${t(SubmissionTranslKey.CERTIFICATION)}.pdf`;
                BlobUtils.downloadBlob(documentBlob, fileName);
            })
            .catch((err) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err))
            .finally(() => {
                setCertButtonInProgress(false);
            });
    }

    function downloadSubmittedDocument(submissionId: number, documentName: string) {
        submissionClient.downloadSubmittedDocument(submissionId)
            .then((documentBlob) => {
                BlobUtils.downloadBlob(documentBlob, documentName + ".xml");
            })
            .catch((err) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err));
    }

    function downloadPrintableDocument(submissionId: number, documentName: string) {
        submissionClient.downloadSubmittedPrintableDocument(submissionId)
            .then((documentBlob) => {
                BlobUtils.downloadBlob(documentBlob, documentName + ".pdf");
            })
            .catch((err) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err));
    }

    if (!submissionIdIsValid) {
        navigate(SUBMISSIONS);
        return null;
    }

    if (!submission) {
        return null;
    }

    const showPaymentInfo = submission.paymentRequired !== false && authorizationSucceeded;
    const showPaymentInformationPdfButton = !!paymentInformation
        && paymentInformation.format !== PaymentInformationFormat.ONLY_TEXTS
        && !!paymentInformation.accountNumber;

    let buttons: React.ReactNode;
    if (submission.digitalActionAuthorization?.authorizationResult === DigitalActionAuthorizationResult.SUCCESS) {
        buttons = (
            <>
                <Button variant={"secondary"}
                        inProgress={certButtonInProgress}
                        disabled={!(certificationAttachmentResult.isSuccess || certNotStampedEnabled)}
                        onClick={() => certificationAttachmentResult.isSuccess
                            ? downloadSubmissionCertification(submission.id, submission.submissionNumber)
                            : downloadCertificationNotStamped(submission.id, submission.submissionNumber)}>
                    {t(SubmissionTranslKey.DOWNLOAD_SUBMISSION_CERTIFICATION)}
                </Button>
                <Button variant={"secondary"}
                        onClick={() => downloadSubmittedDocument(submission.id, submission.submissionNumber)}>
                    {t(SubmissionTranslKey.DOWNLOAD_SUBMITTED_DOCUMENT)}
                </Button>
                <Button variant={"primary"}
                        onClick={() => downloadPrintableDocument(submission.id, submission.submissionNumber)}>
                    {t(SubmissionTranslKey.DOWNLOAD_PRINTABLE_DOCUMENT)}
                </Button>
                {showPaymentInformationPdfButton && (
                        <Button onClick={() => downloadPaymentInformationPdf(submission.id)} inProgress={pdfInProgress}>
                            {t(PaymentTranslKey.DOWNLOAD_PAYMENT_INFORMATION_PDF)}
                        </Button>
                )}
            </>
        );
    } else {
        buttons = (
            <Button variant={"primary"}
                    inProgress={reinitMutation.isPending}
                    onClick={() => reinitMutation.mutate(submission.id)}>
                {t(SubmissionTranslKey.RETRY_AUTHORIZATION)}
            </Button>
        );
    }

    return (
        <Page title={t(SubmissionTranslKey.SUBMISSION_AUTHORIZATION_RESULT)}>
            <AuthorizationServiceUnavailableDialog open={showUnavailableDialog} onClose={() => navigate(DASHBOARD)} />
            <SubmissionNotReadyForAuthorizationDialog open={showNotReadyDialog} onClose={() => navigate(DASHBOARD)} />
            <Stack spacing={2} padding={1}>
                <DetailsTable header={t(SubmissionTranslKey.AUTHORIZATION_RESULT)}>
                    <DetailsTextRow label={t(SubmissionTranslKey.DIGITAL_ACTION_ID)} value={submission.digitalActionAuthorization?.digitalActionId}/>
                    <DetailsTextRow label={t(SubmissionTranslKey.AUTHORIZATION_RESULT)}
                                    color={submission.digitalActionAuthorization?.authorizationResult === DigitalActionAuthorizationResult.SUCCESS ? "success" : "error"}
                                    value={DigitalActionAuthorizationResultFormatter.formatResult(submission.digitalActionAuthorization?.authorizationResult, t)}/>
                </DetailsTable>

                <DetailsTable header={t(SubmissionTranslKey.SUBMISSION_DETAILS)}>
                    <DetailsTextRow label={t(SubmissionTranslKey.DOCUMENT_NUMBER)} value={submission.submissionNumber}/>
                    <DetailsTextRow label={t(SubmissionTranslKey.DOCUMENT_TYPE)}
                                    value={t(submission.documentMetadata.type?.code ?? '', {ns: CatalogType.DOCUMENT_TYPE})}/>
                    <DetailsTextRow label={t(SubmissionTranslKey.SUBMITTED_AT)} value={DateTimeFormatter.formatDateTime(submission.submittedAt)}/>
                </DetailsTable>

                {showPaymentInfo && <PaymentInformationPanel submissionId={submission.id} authorizationSucceeded={authorizationSucceeded}/>}

                <ButtonBar>
                    {buttons}
                </ButtonBar>
            </Stack>
        </Page>
    );
}
